'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  Drawer,
  IconButton,
  Link,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { ChangeEvent, CSSProperties, FormEvent } from 'react';
import { useEffect, useState } from 'react';
import * as v from 'valibot';
import { auth0 } from '@repo/auth0';
import { env } from '@repo/env';
import { useAnalyticsPageView } from '@repo/segment/Analytics/AnalyticsPageView';
import { ElementViewed } from '@repo/segment/Analytics/ElementViewed';
import { useFormAction } from '@repo/segment/Analytics/FormAction';
import { useAnonymousId } from '@repo/segment/components/AnonymousIdProvider';
import { Logo } from '../Logo';
import { theme } from '../NerdWalletThemeProvider';
import type { PromotionalBannerSignUpOptions } from '../PromotionalBanner/types';
import { FORM_NAME, MODAL_ENTITY_NAME, MODAL_SECTION_NAME } from './segment_constants';

const EMAIL_SCHEMA = v.pipe(v.string(), v.email());

const ILLUSTRATION = {
  // 1x CDN width ≈ panel content width at min dialog size (1110 − 640 form − 48 pad).
  path: 'cdn/banking/account-creation-banner-images/complete-rate-monitoring-form-logo-cropped.png',
  size: 420,
} as const;

// Shared Cloudflare options — same as PromotionalBannerImage / BankingAssessmentBanner.
const CF_BASE_OPTS = 'format=auto,fit=scale-down,quality=85,sharpen=1';

function cfIllustration(extra: string) {
  return `${env.NEXT_PUBLIC_NW_URL}/cdn-cgi/image/${extra},${CF_BASE_OPTS}/${ILLUSTRATION.path}`;
}

const ILLUSTRATION_SRC = cfIllustration(`width=${ILLUSTRATION.size}`);
const ILLUSTRATION_SRCSET = [ILLUSTRATION.size, ILLUSTRATION.size * 2]
  .map(w => `${cfIllustration(`width=${w}`)} ${w}w`)
  .join(', ');

type PromotionalButtonModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Server-threaded auth0 sign-up attributes. The client-only `anonymousUserId` /
   * `driverPageViewId` and the `driverElement` are added here before sign-up.
   */
  signUpOptions: PromotionalBannerSignUpOptions;
};

export function PromotionalButtonModal({
  isOpen,
  onClose,
  signUpOptions,
}: PromotionalButtonModalProps) {
  const anonymousUserId = useAnonymousId();
  const { pageViewId } = useAnalyticsPageView();
  const { fireFormAction } = useFormAction(FORM_NAME);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  // Modal impression.
  useEffect(() => {
    if (isOpen) {
      fireFormAction('viewed');
    }
  }, [isOpen, fireFormAction]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (error) setError(null);
  };

  const handleBlur = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    // NOTE: field_value carries the raw email (PII) per the analytics spec.
    fireFormAction('field-interacted', {
      field: 'email',
      fieldType: 'email',
      fieldValue: trimmedEmail,
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!v.safeParse(EMAIL_SCHEMA, trimmedEmail).success) {
      setError('Please enter a valid email address.');
      fireFormAction('error');
      return;
    }

    fireFormAction('submitted');
    // Routes to sign-up with the email pre-populated via loginHint.
    auth0.signUp({
      ...signUpOptions,
      driverElement: FORM_NAME,
      anonymousUserId: anonymousUserId ?? '',
      driverPageViewId: pageViewId,
      loginHint: trimmedEmail,
    });
  };

  const renderCloseButton = (onCloseClick: () => void) => (
    <IconButton
      disableFocusRipple
      disableTouchRipple
      disableRipple
      aria-label="Close"
      onClick={onCloseClick}
      sx={{ position: 'absolute', top: '8px', right: '8px', color: 'black' }}
    >
      <CloseIcon />
    </IconButton>
  );

  const formContent = (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '24px',
        padding: {
          xs: '32px 32px',
          md: '0',
        },
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', columnGap: '6px' }}
      >
        <Logo />
      </Box>
      <Typography
        id="track-savings-modal-title"
        sx={{
          fontSize: {
            xs: '24px',
            md: '34px',
          },
          fontWeight: 700,
          lineHeight: '120%',
          color: 'text.primary',
          textAlign: 'center',
        }}
      >
        We&apos;ll track savings rates so you don&apos;t have to.
      </Typography>
      <Typography
        sx={{ color: 'neutral.darker', fontSize: '16px', fontWeight: 325, lineHeight: '150%' }}
      >
        Create a free account, link your bank account, and we&apos;ll notify you when rates change.
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: {
            xs: '32px',
            md: '16px',
          },
        }}
      >
        <TextField
          type="email"
          placeholder="Email"
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(error)}
          helperText={error ?? ''}
          fullWidth
          autoFocus
          slotProps={{
            input: {
              'aria-label': 'Email address',
              'aria-invalid': Boolean(error),
            },
            formHelperText: {
              role: 'alert',
              sx: {
                marginLeft: '0',
              },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
            '& .MuiOutlinedInput-root:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: 'blues.lighter',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'blues.lighter',
            },
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: 'reds.main',
            },
            '& .MuiFormHelperText-root.Mui-error': {
              color: 'reds.main',
            },
            marginBottom: {
              md: 'var(--modal-input-mb)',
            },
          }}
          style={{ '--modal-input-mb': error ? '0' : '16px' } as CSSProperties}
        />
        <Button
          sx={{
            borderRadius: '4px',
            padding: { md: '12px 32px' },
          }}
          disableRipple
          disableFocusRipple
          type="submit"
          variant="contained"
          fullWidth
        >
          Continue
        </Button>
      </Box>
      <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
        By registering, I agree to NerdWallet&apos;s{' '}
        <Link href={`${env.NEXT_PUBLIC_NW_URL}/terms-of-use`}>Terms of Service</Link> and{' '}
        <Link href={`${env.NEXT_PUBLIC_NW_URL}/privacy-policy`}>Privacy Policy</Link>
      </Typography>
    </Box>
  );

  const illustration = (
    <Box
      aria-hidden
      sx={{
        flexShrink: 0,
        width: '40%',
        background: 'linear-gradient(180deg, #E1F0D5 0%, #A2DEA7 100%)',
        padding: '32px 24px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        component="img"
        src={ILLUSTRATION_SRC}
        srcSet={ILLUSTRATION_SRCSET}
        sizes={`(min-width: 1110px) ${ILLUSTRATION.size}px, 100vw`}
        alt=""
        sx={{ width: '100%', height: 'auto', display: 'block' }}
      />
    </Box>
  );

  // md and larger: two-column dialog. xs to before md: bottom drawer (sheet) look.
  if (isDesktop) {
    return (
      <Dialog
        aria-labelledby="track-savings-modal-title"
        open={isOpen}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              minWidth: {
                md: '1110px',
              },
            },
          },
        }}
      >
        <ElementViewed entityName={MODAL_ENTITY_NAME} sectionName={MODAL_SECTION_NAME}>
          {({ fireElementInteracted }) => (
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'row' }}>
              <Box sx={{ padding: '64px', width: '60%' }}>{formContent}</Box>
              {illustration}
              {renderCloseButton(() => {
                fireElementInteracted('modal_close');
                onClose();
              })}
            </Box>
          )}
        </ElementViewed>
      </Dialog>
    );
  }

  return (
    <ElementViewed entityName={MODAL_ENTITY_NAME} sectionName={MODAL_SECTION_NAME}>
      {({ fireElementInteracted }) => (
        <Drawer
          aria-labelledby="track-savings-modal-title"
          anchor="bottom"
          open={isOpen}
          onClose={(_event, reason) => {
            // reason is 'backdropClick' | 'escapeKeyDown' → snake_case interaction_type.
            const interactionType = reason.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
            fireElementInteracted(interactionType);
            onClose();
          }}
          slotProps={{
            paper: {
              sx: {
                height: '50vh',
                overflowY: 'auto',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
              },
            },
          }}
        >
          {formContent}
        </Drawer>
      )}
    </ElementViewed>
  );
}
